/* =========================================================================
   SAMSARA — SECTION-PRODUCT-CYCLE.JS
   Sprint 3 / T5 SIGNATURE — Cycle timeline live-calculated.

   Modules:
     1. Date calculation Europe/Bucharest TZ:
        - Imported: from data-imported-date attribute
        - Roasted: most recent past Saturday (or coming Saturday if today is Saturday <10:00)
        - Sweet spot: roast +7 to +14 zile (range)
        - Bea-l până: roast +30 zile
     2. Format dates RO style (15 ian 2026)
     3. Relative format ("acum 12 zile", "în 3 zile", "azi")
     4. IO reveal one-shot pe section

   Vanilla, zero deps. CLAUDE.md JS conventions §3.
   ========================================================================= */

(function () {
  'use strict';

  const sections = document.querySelectorAll('[data-product-cycle]');
  if (!sections.length) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const RO_MONTHS_SHORT = [
    'ian', 'feb', 'mar', 'apr', 'mai', 'iun',
    'iul', 'aug', 'sep', 'oct', 'noi', 'dec'
  ];

  function getBucharestParts(date) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Europe/Bucharest',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      hour12: false,
      weekday: 'short'
    }).formatToParts(date);

    const get = function (type) {
      const part = parts.find(function (p) { return p.type === type; });
      return part ? part.value : '';
    };

    const weekdayMap = {
      sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6
    };

    return {
      year: parseInt(get('year'), 10),
      month: parseInt(get('month'), 10),
      day: parseInt(get('day'), 10),
      hour: parseInt(get('hour'), 10),
      weekday: weekdayMap[get('weekday').toLowerCase()] || 0
    };
  }

  function bucharestNow() {
    const p = getBucharestParts(new Date());
    return new Date(Date.UTC(p.year, p.month - 1, p.day));
  }

  /* Prăjire mutată o săptămână (theme setting moved_roast_date, YYYY-MM-DD) — aceleași faze
     ca snippets/moved-roast.liquid, la nivel de zi (cutoff-ul e seara, 23:59): de sâmbăta de
     dinainte până în ziua dinaintea prăjirii mutate → data mutată; de atunci până duminică →
     sâmbăta din săptămâna următoare (cea din săptămâna mutată nu are loc). Altfel null. */
  function movedRoastDate(movedStr, now) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(movedStr || '');
    if (!m) return null;
    const DAYMS = 86400000;
    const moved = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
    const weekStart = moved.getTime() - ((moved.getUTCDay() + 6) % 7) * DAYMS;
    const skipped = weekStart + 5 * DAYMS; // sâmbăta — ciclul de aici e fixat pe sâmbătă
    const t = now.getTime();
    if (t >= skipped - 7 * DAYMS && t < moved.getTime()) {
      return { date: moved, isFuture: true, isToday: false };
    }
    if (t >= moved.getTime() && t <= weekStart + 6 * DAYMS) {
      return { date: new Date(skipped + 7 * DAYMS), isFuture: true, isToday: false };
    }
    return null;
  }

  function calculateRoastDate(overrideStr, movedStr) {
    /* Roast cycle pe sâmbătă (weekday 6 Sat, 0 Sun, 1-5 Mon-Fri).
       Mon-Fri (1-5) → roast date = next Saturday (upcoming, look-forward).
       Sat (6) → today = roast day.
       Sun (0) → roast date = yesterday (just roasted).
       Override pauză/concediu (theme setting next_roast_override, YYYY-MM-DD):
       dacă e setat și e AZI sau în viitor → prima prăjire = data aia.
       Dacă data a trecut (Iustin a uitat să golească) → ignorat, revine automat
       la calculul normal. Returns { date, isFuture, isToday }. */
    const now = bucharestNow();
    if (overrideStr && /^\d{4}-\d{2}-\d{2}$/.test(overrideStr)) {
      const parts = overrideStr.split('-');
      const od = new Date(Date.UTC(+parts[0], +parts[1] - 1, +parts[2]));
      if (od.getTime() >= now.getTime()) {
        return { date: od, isFuture: od.getTime() > now.getTime(), isToday: od.getTime() === now.getTime() };
      }
    }
    const moved = movedRoastDate(movedStr, now);
    if (moved) return moved;
    const nowParts = getBucharestParts(new Date());
    const dow = nowParts.weekday;
    const d = new Date(now);
    let isFuture = false;
    let isToday = false;

    if (dow === 6) {
      isToday = true;
    } else if (dow === 0) {
      d.setUTCDate(d.getUTCDate() - 1); // last Sat = yesterday
    } else {
      // Mon-Fri → forward to next Sat
      const daysAhead = 6 - dow;
      d.setUTCDate(d.getUTCDate() + daysAhead);
      isFuture = true;
    }
    return { date: d, isFuture: isFuture, isToday: isToday };
  }

  function addDays(date, n) {
    const d = new Date(date);
    d.setUTCDate(d.getUTCDate() + n);
    return d;
  }

  function formatRO(date) {
    const day = date.getUTCDate();
    const month = RO_MONTHS_SHORT[date.getUTCMonth()];
    const year = date.getUTCFullYear();
    return day + ' ' + month + ' ' + year;
  }

  function formatRelative(targetDate, now) {
    const diffMs = targetDate.getTime() - now.getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'azi';
    if (diffDays === 1) return 'mâine';
    if (diffDays === -1) return 'ieri';
    if (diffDays > 1) return 'în ' + diffDays + ' zile';
    return 'acum ' + Math.abs(diffDays) + ' zile';
  }

  function parseImportedDate(str) {
    if (!str) return null;
    // Expect ISO format YYYY-MM-DD
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(str);
    if (!m) return null;
    return new Date(Date.UTC(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10)));
  }

  function init(section) {
    const importedDateStr = section.dataset.importedDate;
    const importedDate = parseImportedDate(importedDateStr);
    const roast = calculateRoastDate(section.dataset.roastOverride, section.dataset.movedRoast);
    const roastDate = roast.date;
    // Sweet spot window: dinamic per profile (washed: +7/+14, co-ferment: +10/+14).
    // Liquid sets data-sweet-start / data-sweet-end based on coffee.process.
    const sweetStartDays = parseInt(section.dataset.sweetStart, 10) || 7;
    const sweetEndDays = parseInt(section.dataset.sweetEnd, 10) || 14;
    const sweetStart = addDays(roastDate, sweetStartDays);
    const sweetEnd = addDays(roastDate, sweetEndDays);
    const consumeBy = addDays(roastDate, 30);
    const today = bucharestNow();

    const setText = function (selector, text) {
      const el = section.querySelector(selector);
      if (el) el.textContent = text;
    };

    if (importedDate) {
      setText('[data-product-cycle-date="imported"]', formatRO(importedDate));
      setText('[data-product-cycle-relative="imported"]', formatRelative(importedDate, today));
    }

    /* Label dinamic: "Următoarea prăjire" (future) vs "Prăjit" (past/today).
       Step e marked --active doar dacă e azi sau în trecut recent. */
    const roastLabel = roast.isFuture ? 'Următoarea prăjire' : 'Prăjit';
    setText('[data-product-cycle-step="roasted"] .product-cycle__step-label', roastLabel);
    setText('[data-product-cycle-date="roasted"]', formatRO(roastDate));
    setText('[data-product-cycle-relative="roasted"]', formatRelative(roastDate, today));

    /* Marker visual state: --active = future roast (highlight what's next) */
    const roastStep = section.querySelector('[data-product-cycle-step="roasted"]');
    if (roastStep) {
      roastStep.classList.toggle('product-cycle__step--active', !roast.isFuture);
      roastStep.classList.toggle('product-cycle__step--upcoming', roast.isFuture);
    }

    setText('[data-product-cycle-date="sweet"]', formatRO(sweetStart) + ' – ' + formatRO(sweetEnd));
    setText('[data-product-cycle-relative="sweet"]', formatRelative(sweetStart, today));

    setText('[data-product-cycle-date="consume"]', formatRO(consumeBy));
    setText('[data-product-cycle-relative="consume"]', formatRelative(consumeBy, today));

    // Reveal observer
    if (reducedMotion || typeof IntersectionObserver === 'undefined') {
      section.classList.add('is-revealed');
      return;
    }

    const observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            section.classList.add('is-revealed');
            obs.unobserve(section);
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -10% 0px' }
    );

    observer.observe(section);
  }

  function bootstrap() {
    sections.forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
